import * as Sentry from '@sentry/react';

import { isBenignDomMutationError } from '@/utils/isBenignDomMutationError';

const RECOVERY_BANNER_ID = 'inspect-app-recovery-banner';

function isHighValueInspectionRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  // Only treat detail/module routes as high-value:
  // /inspections/:id or /inspections/:id/...
  // (Not the list page at exactly /inspections)
  return /^\/inspections\/[^/]+(\/.*)?$/.test(path);
}

/**
 * Shows a full-page recovery UI when an unhandled rejection or script error
 * occurs. React error boundaries don't catch these, so the page can go blank.
 * This gives the user a way to reload or navigate without losing the tab.
 */
function showRecoveryUI(): void {
  if (typeof document === 'undefined' || document.getElementById(RECOVERY_BANNER_ID)) {
    return;
  }
  const root = document.getElementById('root');
  const wrap = document.createElement('div');
  wrap.id = RECOVERY_BANNER_ID;
  wrap.innerHTML = `
    <div style="
      position: fixed; inset: 0; z-index: 99999;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      padding: 2rem; font-family: system-ui, sans-serif; background: #f5f5f5; color: #333;
    ">
      <h1 style="font-size: 1.5rem; margin-bottom: 1rem; text-align: center;">Something went wrong</h1>
      <p style="font-size: 1rem; margin-bottom: 1.5rem; text-align: center; max-width: 320px;">
        Your captured data may still be saved. Go to Inspections to check, or try reloading.
      </p>
      <div style="display: flex; gap: 1rem; flex-wrap: wrap; justify-content: center;">
        <button id="recovery-goto-inspections" type="button" style="
          padding: 0.75rem 1.5rem; font-size: 1rem; font-weight: 600; border: none; border-radius: 8px;
          background: #1976d2; color: white; cursor: pointer;
        ">Go to Inspections</button>
        <button id="recovery-reload" type="button" style="
          padding: 0.75rem 1.5rem; font-size: 1rem; font-weight: 600; border: 1px solid #1976d2;
          border-radius: 8px; background: white; color: #1976d2; cursor: pointer;
        ">Reload page</button>
      </div>
    </div>
  `;
  wrap.querySelector('#recovery-goto-inspections')?.addEventListener('click', () => {
    window.location.href = '/inspections';
  });
  wrap.querySelector('#recovery-reload')?.addEventListener('click', () => {
    window.location.reload();
  });
  (root?.parentElement ?? document.body).appendChild(wrap);
}

/**
 * Install global handlers for unhandled rejections and errors.
 * Call once from main.tsx so async errors and script errors don't leave a blank page.
 */
export function installGlobalErrorHandlers(): void {
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    const isBenignDomError = isBenignDomMutationError(error);
    try {
      Sentry.captureException(error, {
        tags: { type: 'unhandledrejection' },
      });
    } catch {
      // Sentry may not be initialized yet
    }
    console.error('Unhandled promise rejection:', event.reason);
    if (isBenignDomError) {
      return;
    }
    // On inspection routes, prefer keeping the user on the page and letting
    // route‑scoped boundaries / local handlers deal with it, to reduce the
    // chance of losing unsaved data.
    if (!isHighValueInspectionRoute()) {
      showRecoveryUI();
    }
    // Don't preventDefault so dev tools still see the error; we've already shown recovery UI
  });

  window.addEventListener('error', (event) => {
    // Only handle script/runtime errors; ignore resource load errors if desired
    if (event.error) {
      const isBenignDomError = isBenignDomMutationError(event.error);
      try {
        Sentry.captureException(event.error, {
          tags: { type: 'window.error' },
        });
      } catch {
        // Sentry may not be initialized yet
      }
      console.error('Uncaught error:', event.error);
      if (isBenignDomError) {
        return;
      }
      if (!isHighValueInspectionRoute()) {
        showRecoveryUI();
      }
    }
  });
}
